'use client';

import { HorizontalOverflowCarouselItem } from '@petsmart-ui/sparky';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import type { Product } from '@libs/analytics';
import {
    createItemsViewEvent,
    getSearchStats,
    TrackingParams,
    trackContentfulClick,
    trackProductTileItemClick,
} from '@libs/analytics';
import { resolveFrefListAttribution } from '@libs/common-next';

import { mapYmalProductsToAnalyticsProducts, mapYmalProductToAnalyticsCategories } from '../lib';
import type { YmalProductTrackerProps } from './ymalProductsCarousel.types';

const PROMO_TITLE = 'You may also like';
const PROMO_LIST_ID = 'product carousel';
const PROMO_VARIANT = 'traditional';
const PROMO_TYPE = 'product carousel - traditional';

// Analytics wants three category levels for the page a shopper clicked from.
// The cart isn't a category page, so all three go out as null.
const CART_PAGE_CATEGORIES = [null, null, null];

export function YmalProductTracker({
    children,
    position,
    product,
    frefAttribution,
    onView,
}: Readonly<YmalProductTrackerProps>) {
    const pathname = usePathname();
    const { ref, inView } = useInView({ threshold: 0.2, skip: !onView });

    useEffect(() => {
        if (inView) onView?.();
    }, [inView, onView]);

    const handleClick = () => {
        if (frefAttribution) {
            // Persist the fref-style finding method (e.g. `rplp_fbt_2`) so cart events can attribute
            // this SKU back to its carousel and position even if the `?fref=` query param is lost.
            globalThis.sessionStorage.setItem(
                'pdpFindingMethod',
                `${frefAttribution.listId}_${frefAttribution.listName}_${position}`
            );
        } else {
            const referrer = TrackingParams.PRODUCT_CAROUSEL_REFERER;
            if (referrer) {
                globalThis.sessionStorage.setItem('pdpFindingMethod', String(referrer));
            }
        }

        const analyticsProduct = mapYmalProductsToAnalyticsProducts([product])[0];
        const listAttribution = frefAttribution ? resolveFrefListAttribution(frefAttribution) : undefined;

        const { listName, listId, categories, searchTerm, listPosition, pdpType } = listAttribution
            ? {
                  listName: listAttribution.item_list_name,
                  listId: listAttribution.item_list_id,
                  categories: CART_PAGE_CATEGORIES,
                  searchTerm: null,
                  listPosition: position,
                  pdpType: null,
              }
            : {
                  listName: PROMO_TITLE,
                  listId: PROMO_LIST_ID,
                  categories: mapYmalProductToAnalyticsCategories(product),
                  searchTerm: getSearchStats().searchTerm,
                  listPosition: undefined,
                  pdpType: undefined,
              };

        trackProductTileItemClick({
            product: analyticsProduct as unknown as Product,
            ...(listPosition !== undefined && { listPosition }),
            pdpType,
            ...createItemsViewEvent([analyticsProduct], listName, listId, categories, searchTerm),
        });

        trackContentfulClick({
            position,
            linkText: product.name,
            linkUrl: product.url,
            promoTitle: PROMO_TITLE,
            promoVariant: PROMO_VARIANT,
            promoDesc: 'you-may-also-like-products-carousel',
            promoType: PROMO_TYPE,
            promoOrigin: pathname,
        });
    };

    return (
        <HorizontalOverflowCarouselItem data-testid={product.name} onClick={handleClick}>
            {onView ? <div ref={ref}>{children}</div> : children}
        </HorizontalOverflowCarouselItem>
    );
}
